import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser, Check } from 'lucide-react';

/**
 * Reusable Signature Pad Component
 * Allows users to draw their signature with mouse or touch
 */
export const SignaturePad = ({ 
  onSave, 
  onClear,
  width = 400, 
  height = 150,
  strokeColor = '#1e40af',
  strokeWidth = 2,
  className = ''
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);
  const [hasContent, setHasContent] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set canvas size
    canvas.width = width;
    canvas.height = height;
    
    // Set white background
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }, [width, height]);

  // Get point coordinates from mouse/touch event
  const getCanvasPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const point = getCanvasPoint(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    setLastPoint(point);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const point = getCanvasPoint(e, canvas);
    
    // Smooth line drawing with quadratic curves
    if (lastPoint) {
      const midPoint = {
        x: (lastPoint.x + point.x) / 2,
        y: (lastPoint.y + point.y) / 2
      };
      
      ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, midPoint.x, midPoint.y);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(midPoint.x, midPoint.y);
    }
    
    setLastPoint(point);
    setHasContent(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.stroke();
      }
    }
    setIsDrawing(false);
    setLastPoint(null);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    
    if (onClear) onClear();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasDrawing = imageData.data.some((pixel, i) => {
      // Check if any pixel is not white (255,255,255) or transparent
      if (i % 4 === 0) { // Red channel
        const r = pixel;
        const g = imageData.data[i + 1];
        const b = imageData.data[i + 2];
        return r !== 255 || g !== 255 || b !== 255;
      }
      return false;
    });
    
    if (!hasDrawing) {
      return null;
    }
    
    const signatureData = canvas.toDataURL('image/png');
    if (onSave) onSave(signatureData);
    return signatureData;
  };

  return (
    <div className={`signature-pad ${className}`}>
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-1 bg-white">
        <canvas
          ref={canvasRef}
          className="cursor-crosshair touch-none w-full"
          style={{ maxWidth: width, height: height }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex justify-between items-center mt-2">
        <p className="text-xs text-slate-500">
          Draw your signature above
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearSignature}
            className="text-slate-600"
            data-testid="signature-pad-clear"
          >
            <Eraser className="w-4 h-4 mr-1" />
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={saveSignature}
            disabled={!hasContent}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="signature-pad-save"
          >
            <Check className="w-4 h-4 mr-1" />
            Save Signature
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
